/**
 * Shared Group schedule form helpers (calendar GroupAppointmentScheduleFields ↔ cohort form).
 */
import { formatDateOnlyLocal, parseDateOnlyLocal } from '@/lib/dateHelpers';
import {
  hhmmToMinutes,
  type GroupRecurrenceOption,
  type GroupSetupFormValues,
  listGroupSetupSessionDates,
} from '@/lib/groupTherapy';
import { parseTimeLabel12h } from '@/features/calendar/providerAvailabilityValidation';
import type {
  GroupAppointmentScheduleFieldsValue,
  GroupScheduleModality,
} from '@/pages/calendar/components/GroupAppointmentScheduleFields';

export const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export function hhmmToAmPm(hhmm: string): string {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!match) return '6:00 PM';
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return '6:00 PM';
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
}

export function amPmToHhmm(label: string): string {
  const parsed = parseTimeLabel12h(label);
  if (!parsed) return '18:00';
  return `${String(parsed.hour).padStart(2, '0')}:${String(parsed.minute).padStart(2, '0')}`;
}

export function bareLocationId(ref: string | undefined): string {
  if (!ref) return '';
  return ref.replace(/^Location\//, '');
}

export function calendarDaysToNames(days: string[]): string[] {
  return days
    .map((d) => {
      const idx = Number(d);
      return Number.isFinite(idx) ? WEEKDAY_NAMES[idx] : undefined;
    })
    .filter((n): n is (typeof WEEKDAY_NAMES)[number] => Boolean(n));
}

export function namesToCalendarDays(days: string[]): string[] {
  return days
    .map((name) => {
      const idx = WEEKDAY_NAMES.findIndex(
        (n) => n.toLowerCase() === name.trim().toLowerCase(),
      );
      return idx >= 0 ? String(idx) : undefined;
    })
    .filter((d): d is string => Boolean(d));
}

function addDaysYmd(ymd: string, days: number): string {
  const d = parseDateOnlyLocal(ymd);
  if (!d) return ymd;
  return formatDateOnlyLocal(
    new Date(d.getFullYear(), d.getMonth(), d.getDate() + days),
  );
}

function endAmPmFromStored(values: GroupSetupFormValues): string {
  if (values.endTime?.trim()) return hhmmToAmPm(values.endTime);
  // UI-only default window: 6–7 PM when end is unset.
  const start = hhmmToMinutes(values.startTime || '18:00');
  if (start == null) return '7:00 PM';
  const end = (start + 60) % (24 * 60);
  const hour = Math.floor(end / 60);
  const minute = end % 60;
  return hhmmToAmPm(`${hour}:${String(minute).padStart(2, '0')}`);
}

export function scheduleFieldsFromGroup(
  values: GroupSetupFormValues,
  firstSiteId: string,
): GroupAppointmentScheduleFieldsValue {
  const siteId = bareLocationId(values.locationReference);
  const modality: GroupScheduleModality =
    values.modality === 'in-person' || values.modality === 'hybrid'
      ? values.modality
      : 'telehealth';
  const startAmPm = hhmmToAmPm(values.startTime || '18:00');
  const isCustom = values.recurrence === 'custom';
  const sessionDates = [...(values.sessionDates ?? [])].filter(Boolean).sort();
  const hasSchedule = Boolean(values.startDate && values.startTime);
  const recurringEnabled =
    !isCustom
    && (hasSchedule
      ? (values.recurrence !== undefined
        || (values.daysOfWeek?.length ?? 0) > 0
        || Boolean(values.endDate)
        || (values.sessionCount ?? 0) > 0)
      : !values.id);

  return {
    date: values.startDate
      ? parseDateOnlyLocal(values.startDate)
      : sessionDates[0]
        ? parseDateOnlyLocal(sessionDates[0])
        : null,
    startTime: startAmPm,
    endTime: endAmPmFromStored(values),
    recurringEnabled,
    scheduleMode: isCustom ? 'custom' : 'recurring',
    sessionDates,
    recurringInterval: values.recurrence === 'biweekly' ? 2 : 1,
    recurringTimeframe: values.recurrence === 'monthly' ? 'month' : 'week',
    recurringDaysOfWeek: namesToCalendarDays(values.daysOfWeek ?? []).length
      ? namesToCalendarDays(values.daysOfWeek ?? [])
      : values.startDate
        ? [String(parseDateOnlyLocal(values.startDate)?.getDay() ?? 1)]
        : values.daysOfWeek?.length
          ? namesToCalendarDays(values.daysOfWeek)
          : ['1'],
    recurringEndType: values.endDate && !values.sessionCount ? 'date' : 'occurrences',
    recurringEndDate: values.endDate ? parseDateOnlyLocal(values.endDate) : null,
    recurringOccurrences: values.sessionCount ?? 8,
    modality,
    locationId:
      modality === 'telehealth'
        ? 'telehealth'
        : siteId || firstSiteId,
    telehealthFromOffice:
      modality === 'telehealth' && Boolean(siteId || values.roomId),
    officeForTelehealth: siteId || firstSiteId,
    roomId: values.roomId ?? null,
    deviceIds: values.deviceIds ?? [],
  };
}

export function applyScheduleFieldsToGroup(
  base: GroupSetupFormValues,
  schedule: GroupAppointmentScheduleFieldsValue,
  sites: { id: string; name: string }[],
): GroupSetupFormValues {
  const startTime = amPmToHhmm(schedule.startTime);
  const endTime = amPmToHhmm(schedule.endTime);
  const isCustom =
    schedule.scheduleMode === 'custom' || !schedule.recurringEnabled;

  let recurrence: GroupRecurrenceOption = 'weekly';
  let startDate = schedule.date ? formatDateOnlyLocal(schedule.date) : '';
  let endDate: string | undefined;
  let sessionCount: number | undefined;
  let sessionDates: string[] | undefined;
  let daysOfWeek: string[] = [];

  if (isCustom) {
    recurrence = 'custom';
    const ymds = (schedule.sessionDates.length
      ? schedule.sessionDates
      : schedule.date
        ? [formatDateOnlyLocal(schedule.date)]
        : []
    )
      .map((d) => (typeof d === 'string' ? d.slice(0, 10) : formatDateOnlyLocal(d)))
      .filter(Boolean)
      .sort();
    sessionDates = ymds;
    startDate = ymds[0] ?? startDate;
    endDate = ymds.length ? ymds[ymds.length - 1] : undefined;
    sessionCount = ymds.length || undefined;
    daysOfWeek = startDate
      ? [WEEKDAY_NAMES[parseDateOnlyLocal(startDate)?.getDay() ?? 1]]
      : [];
  } else {
    if (schedule.recurringTimeframe === 'month') {
      recurrence = 'monthly';
    } else if (schedule.recurringInterval === 2) {
      recurrence = 'biweekly';
    } else {
      recurrence = 'weekly';
    }
    daysOfWeek =
      schedule.recurringTimeframe === 'week'
        ? calendarDaysToNames(schedule.recurringDaysOfWeek)
        : startDate
          ? [WEEKDAY_NAMES[schedule.date?.getDay() ?? 1]]
          : [];
    if (schedule.recurringEndType === 'date' && schedule.recurringEndDate) {
      endDate = formatDateOnlyLocal(schedule.recurringEndDate);
      sessionCount = undefined;
    } else {
      sessionCount = Math.max(1, schedule.recurringOccurrences);
      const weeks =
        schedule.recurringTimeframe === 'month'
          ? sessionCount * 5
          : sessionCount * Math.max(1, schedule.recurringInterval);
      const outerBound = startDate ? addDaysYmd(startDate, weeks * 7 + 7) : undefined;
      // Land endDate on the Nth occurrence; a padded bound makes the series bot
      // generate the extra sessions that fall inside it.
      const occurrences = outerBound
        ? listGroupSetupSessionDates(
            {
              startDate,
              endDate: outerBound,
              enrollmentMode: 'closed',
              recurrence,
              daysOfWeek,
              sessionDates: [],
            },
            { maxDates: sessionCount },
          )
        : [];
      endDate = occurrences[occurrences.length - 1] ?? outerBound;
    }
    sessionDates = [];
  }

  const modality = schedule.modality;
  const usingOffice =
    modality === 'telehealth' && schedule.telehealthFromOffice;
  const locationId =
    modality === 'telehealth'
      ? usingOffice
        ? schedule.officeForTelehealth
        : ''
      : schedule.locationId === 'telehealth'
        ? ''
        : schedule.locationId;
  const site = sites.find((s) => s.id === locationId);

  return {
    ...base,
    startDate,
    startTime,
    endTime,
    recurrence,
    daysOfWeek,
    endDate,
    sessionCount,
    sessionDates,
    modality,
    locationReference: locationId ? `Location/${locationId}` : undefined,
    locationDisplay: site?.name,
    roomId: schedule.roomId ?? undefined,
    roomDisplay: undefined,
    deviceIds: schedule.deviceIds,
    timezone: base.timezone || 'America/Los_Angeles',
  };
}
